import {
  includesDispatchAgencyType,
  isDispatcherJobTitle,
  isTrainerJobTitle,
} from "./roleMap";
import {
  isErStakeholderType,
  type RegistrationStepDefinition,
  type RegistrationStepId,
  REGISTRATION_STEP_DEFINITIONS,
} from "./stakeholderSteps";
import { STAKEHOLDER_COMMANDER, STAKEHOLDER_TIER_II } from "./stakeholderConstants";

export type RegistrationStepContext = {
  stakeholderType: string;
  jobTitle: string;
  /** Multi-select agency types from Agency Info (when wired). */
  typeOfAgency?: readonly string[] | string | null;
};

function stakeholderSeesStep(step: RegistrationStepDefinition, stakeholderType: string): boolean {
  return step.visibleTo.length > 0 && step.visibleTo.includes(stakeholderType as never);
}

export function needsDispatchCapabilitiesStep(ctx: RegistrationStepContext): boolean {
  if (isTrainerJobTitle(ctx.jobTitle)) return false;
  return (
    isDispatcherJobTitle(ctx.jobTitle) || includesDispatchAgencyType(ctx.typeOfAgency ?? null)
  );
}

/** Mutual Aid follows Commander/Tier II paths, or any ER path that includes Dispatch capabilities. */
export function needsMutualAidStep(ctx: RegistrationStepContext): boolean {
  if (isTrainerJobTitle(ctx.jobTitle)) return false;
  const st = ctx.stakeholderType;
  if (st === STAKEHOLDER_COMMANDER || st === STAKEHOLDER_TIER_II) return true;
  if (isErStakeholderType(st) && needsDispatchCapabilitiesStep(ctx)) return true;
  return false;
}

/** Agency screens after Organization — Trainer job title skips these and jumps to Additional Questions. */
function isAgencyStepAfterOrganization(stepId: RegistrationStepId): boolean {
  return (
    stepId === "agencyInfo" ||
    stepId === "agencyCapability" ||
    stepId === "agencyProcedure" ||
    stepId === "agencyTraining" ||
    stepId === "dispatchCapabilities" ||
    stepId === "agencyAgreement"
  );
}

/**
 * Resolves the ordered list of wizard steps for the current registrant.
 * Inserts dispatch capabilities after agency info; inserts mutual aid when required.
 * Additional Questions is last for all stakeholder types.
 */
export function getVisibleRegistrationSteps(ctx: RegistrationStepContext): RegistrationStepId[] {
  const out: RegistrationStepId[] = [];
  const showDispatch = needsDispatchCapabilitiesStep(ctx);
  const showMutualAid = needsMutualAidStep(ctx);

  const skipAgencySteps = isTrainerJobTitle(ctx.jobTitle);

  for (const step of REGISTRATION_STEP_DEFINITIONS) {
    if (skipAgencySteps && isAgencyStepAfterOrganization(step.id)) {
      continue;
    }
    if (step.id === "dispatchCapabilities") {
      if (showDispatch) out.push("dispatchCapabilities");
      continue;
    }
    if (step.id === "agencyAgreement") {
      if (showMutualAid) out.push("agencyAgreement");
      continue;
    }
    if (stakeholderSeesStep(step, ctx.stakeholderType)) {
      out.push(step.id);
    }
  }

  return out;
}

export function isLastRegistrationStep(
  ctx: RegistrationStepContext,
  stepId: RegistrationStepId,
): boolean {
  const steps = getVisibleRegistrationSteps(ctx);
  return steps.length > 0 && steps[steps.length - 1] === stepId;
}

export function nextRegistrationStep(
  ctx: RegistrationStepContext,
  currentStepId: RegistrationStepId,
): RegistrationStepId | null {
  const steps = getVisibleRegistrationSteps(ctx);
  const idx = steps.indexOf(currentStepId);
  if (idx < 0 || idx >= steps.length - 1) return null;
  return steps[idx + 1] ?? null;
}

export function previousRegistrationStep(
  ctx: RegistrationStepContext,
  currentStepId: RegistrationStepId,
): RegistrationStepId | null {
  const steps = getVisibleRegistrationSteps(ctx);
  const idx = steps.indexOf(currentStepId);
  if (idx <= 0) return null;
  return steps[idx - 1] ?? null;
}

/** First profile step after account (for express return: step id > account). */
export function firstProfileStepId(ctx: RegistrationStepContext): RegistrationStepId | null {
  const steps = getVisibleRegistrationSteps(ctx);
  return steps.find((id) => id !== "quickCode" && id !== "account") ?? null;
}
